// Relationship categories — used for form field visibility and validation.
export const RELATIONSHIPS_WITH_LOCATION = [
  'personOrOrganization',
  'mortgageeCoOwner',
  'managerOrLessorOfPremises',
  'managerOrLessorOfPremisesCommercial',
  'landlord',
  'propertyCoverages'
];
export const RELATIONSHIPS_WITH_EQUIPMENT = ['lessorOfLeasedEquipment'];
export const RELATIONSHIPS_WITH_LOCATION_AND_RISKS = ['trustCorpsEtc'];
export const RELATIONSHIPS_WITH_LOCATION_RISKS_AND_EQUIPMENT = ['landContractHolder'];
export const RELATIONSHIPS_WITH_DESCRIPTION_ONLY = ['billToOther'];
// Loss Payee associates the same entity set as Additional Interest — locations, risks, equipment,
// inland marine (SPP), and related private structures. Equipment is shown only on full Farm; the
// policy-type mask hides it on Home/Rental via isPartyEquipmentVisible. (#32520/#32521/#32525)
export const RELATIONSHIPS_WITH_ALL_FIELDS = ['partnerOrCoOwner', 'additionalInterest', 'lossPayee'];
export const RELATIONSHIPS_WITH_RESIDENT_INFO = ['occupant'];

// Liability-dependent visibility for relationship options in the dropdown.
// 'P' = personal, 'C' = commercial. If a type is not listed here, it shows always.
const PERSONAL_ONLY_RELATIONSHIPS = [
  'personOrOrganization',
  'partnerOrCoOwner',
  'occupant',
  'managerOrLessorOfPremises'
];
// mortgageeCoOwner is the dropdown ID for "Additional Insureds, GL-108" — GL-108 is
// a Commercial Liability filed form, so the option is restricted to Commercial. (#30592)
const COMMERCIAL_ONLY_RELATIONSHIPS = ['managerOrLessorOfPremisesCommercial', 'mortgageeCoOwner'];
const LIABILITY_ON_RELATIONSHIPS = ['lessorOfLeasedEquipment'];

/**
 * Filters the farmInterestedPartyTypes options based on the current liability state and
 * the parties already on the policy/offer. `billToOther` is hidden if one already exists,
 * since a policy can have at most one BillToOther interested party (the third-party payer).
 * @param {Array} allOptions - full list from lookupsJson.farm.farmInterestedPartyTypes
 * @param {boolean} includeFarmLiability - whether liability coverage is enabled
 * @param {string|undefined} liabilityType - 'P' (personal) or 'C' (commercial)
 * @param {Array} [existingParties] - existing farmInterestedParties on the policy/offer
 * @param {string} [editingPartyId] - id of the party being edited (so it doesn't filter itself out)
 */
export const getFilteredRelationshipOptions = ({
  allOptions,
  includeFarmLiability,
  liabilityType,
  existingParties = [],
  editingPartyId,
  hiddenRelationships = []
}) => {
  const isPersonal = includeFarmLiability && liabilityType === 'P';
  const isCommercial = includeFarmLiability && liabilityType === 'C';
  const isLiabilityOn = isPersonal || isCommercial;
  const hasOtherBillToOther = existingParties.some(
    (party) => party?.relationship === 'billToOther' && party?.id !== editingPartyId
  );

  return allOptions.filter(({ id }) => {
    // Relationships hidden by the policy-type mask (e.g. GL-4002 on rentals, #32043).
    if (hiddenRelationships.includes(id)) {
      return false;
    }
    if (id === 'billToOther' && hasOtherBillToOther) {
      return false;
    }
    if (PERSONAL_ONLY_RELATIONSHIPS.includes(id)) {
      return isPersonal;
    }
    if (COMMERCIAL_ONLY_RELATIONSHIPS.includes(id)) {
      return isCommercial;
    }
    if (LIABILITY_ON_RELATIONSHIPS.includes(id)) {
      return isLiabilityOn;
    }
    return true;
  });
};

// Relationships eligible for the FO-41 add-on toggle.
export const FO41_ELIGIBLE_RELATIONSHIPS = [
  'personOrOrganization',
  'partnerOrCoOwner',
  'occupant',
  'managerOrLessorOfPremises',
  'managerOrLessorOfPremisesCommercial',
  'mortgageeCoOwner'
];

/**
 * Whether the FO-41 add-on toggle should show for a party. FO-41 only applies to certain
 * relationships, and the policy-type mask can suppress it entirely (FO-41 does not apply to
 * Forreston Rental, #32359 — the mask sets hideFO41).
 * @param {string} relationship - the party's relationship id
 * @param {boolean} [hideFO41] - mask flag removing FO-41 for this policy type
 */
export const isFO41ToggleVisible = ({ relationship, hideFO41 = false }) =>
  !hideFO41 && FO41_ELIGIBLE_RELATIONSHIPS.includes(relationship);

/**
 * Whether the "Associated equipment" field should render for a Loss Payee party. Loss Payee
 * associates farm equipment on full Farm, but not on Home/Rental — those products don't schedule
 * farm machinery to a loss payee, so their policy-type mask sets hideLossPayeeEquipment. Only Loss
 * Payee is affected; other all-fields relationships (Additional Interest, Partners/co-owners) keep
 * their equipment field. (#32520 Farm / #32521 Home / #32525 Rental)
 * @param {string} relationship - the party's relationship id
 * @param {boolean} [hideLossPayeeEquipment] - mask flag removing Loss Payee equipment for this policy type
 */
export const isPartyEquipmentVisible = ({ relationship, hideLossPayeeEquipment = false }) =>
  !(hideLossPayeeEquipment && relationship === 'lossPayee');

// Relationships that must have a Description filled in before the party can be saved.
// Issue #30585 / #30734. Covers Additional Insured forms whose endorsements actually
// print a description: GL-70, GL-71, GL 3001, GL 3002, GL 4002, FO-41 standalone.
// GL-72 (occupant) and GL-108 (mortgageeCoOwner) are excluded — those endorsements
// don't carry a description field, so requiring it would block saves with no purpose.
/** Relationships whose entity type (form of interest) staff must supply. Mirrors the entity-type gate. */
export const RELATIONSHIPS_REQUIRING_ENTITY_TYPE = ['partnerOrCoOwner', 'mortgageeCoOwner'];

export const RELATIONSHIPS_WITH_REQUIRED_DESCRIPTION = [
  'propertyCoverages',
  'personOrOrganization',
  'partnerOrCoOwner',
  'managerOrLessorOfPremises',
  'managerOrLessorOfPremisesCommercial',
  'lessorOfLeasedEquipment'
];

export const ENTITY_TYPE_OPTIONS = [
  { id: 'partner', value: 'Partner' },
  { id: 'coOwner', value: 'Co-owner' }
];

export const FO41_DESCRIPTION_PLACEHOLDER =
  'Include details about each property of interest, such as a dwelling, barn, machinery, or other equipment.';

// Label for the description field on the FO-41 add-on toggle (includeFO41) — distinct
// from the standalone propertyCoverages relationship but using the same wording. #30734.
export const FO41_DESCRIPTION_LABEL = 'Describe your interest in the covered property';

export const getDescriptionPlaceholder = (relationship) => {
  if (relationship === 'propertyCoverages') {
    return FO41_DESCRIPTION_PLACEHOLDER;
  }
  return undefined;
};

// Per-form description-field labels. Each label matches the wording of the
// endorsement the AI relationship attaches to. #30734.
export const getDescriptionLabel = (relationship) => {
  if (relationship === 'personOrOrganization') {
    return "Describe your interest in each location you've selected";
  }
  if (relationship === 'propertyCoverages') {
    return FO41_DESCRIPTION_LABEL;
  }
  if (relationship === 'managerOrLessorOfPremises' || relationship === 'managerOrLessorOfPremisesCommercial') {
    return 'Designated Premises (part of the premises leased to Named Insured)';
  }
  if (relationship === 'lessorOfLeasedEquipment') {
    return 'Description of leased equipment';
  }
  if (RELATIONSHIPS_WITH_ALL_FIELDS.includes(relationship)) {
    return 'Describe the interest in each item(s) or location(s) selected';
  }
  return 'Description';
};

export const getVisibilityFlags = (relationship) => ({
  showLocationFields: RELATIONSHIPS_WITH_LOCATION.includes(relationship),
  showEquipmentFields: RELATIONSHIPS_WITH_EQUIPMENT.includes(relationship),
  showLocationAndRiskFields: RELATIONSHIPS_WITH_LOCATION_AND_RISKS.includes(relationship),
  showLocationRiskAndEquipmentFields: RELATIONSHIPS_WITH_LOCATION_RISKS_AND_EQUIPMENT.includes(relationship),
  showDescriptionOnly: RELATIONSHIPS_WITH_DESCRIPTION_ONLY.includes(relationship),
  showAllFields: RELATIONSHIPS_WITH_ALL_FIELDS.includes(relationship),
  showResidentInfo: RELATIONSHIPS_WITH_RESIDENT_INFO.includes(relationship)
});

// Maps each relationship type to the entity field arrays it supports.
// Fields not listed for a relationship are cleared before save to prevent stale data.
const RELATIONSHIP_ENTITY_FIELDS = {
  personOrOrganization: ['locations'],
  mortgageeCoOwner: ['locations'],
  managerOrLessorOfPremises: ['locations'],
  managerOrLessorOfPremisesCommercial: ['locations'],
  landlord: ['locations'],
  occupant: [],
  propertyCoverages: ['locations'],
  lessorOfLeasedEquipment: ['equipment'],
  trustCorpsEtc: ['locations', 'risks'],
  landContractHolder: ['locations', 'risks', 'equipment'],
  billToOther: [],
  partnerOrCoOwner: ['locations', 'risks', 'equipment', 'sppItems', 'rpsItems'],
  additionalInterest: ['locations', 'risks', 'equipment', 'sppItems', 'rpsItems'],
  // Loss Payee mirrors Additional Interest's full entity set. On Home/Rental the equipment field is
  // hidden (isPartyEquipmentVisible), so it simply stays empty there — no product-specific save
  // filtering needed. (#32520/#32521/#32525)
  lossPayee: ['locations', 'risks', 'equipment', 'sppItems', 'rpsItems']
};

export const filterFieldsByRelationship = (fields, relationship) => {
  const allowed = RELATIONSHIP_ENTITY_FIELDS[relationship];
  if (!allowed) {
    return fields;
  }
  return {
    locations: allowed.includes('locations') ? fields.locations : [],
    risks: allowed.includes('risks') ? fields.risks : [],
    equipment: allowed.includes('equipment') ? fields.equipment : [],
    sppItems: allowed.includes('sppItems') ? fields.sppItems : [],
    rpsItems: allowed.includes('rpsItems') ? fields.rpsItems : []
  };
};

/**
 * Converts backend AssociatedEntity[] → flat arrays for chipArray form fields.
 *
 * Backend stores structured references:
 *   [{ entityType: 'LOCATION', locationId: 'L1' },
 *    { entityType: 'RISK', locationId: 'L1', riskId: 'R1' },
 *    { entityType: 'EQUIPMENT', locationId: 'L1', itemId: 'E1' }]
 *
 * Form expects flat arrays with composite IDs matching option helpers:
 *   { locations: ['L1'], risks: ['L1::R1'], equipment: ['L1::R1::E1'], sppItems: ['L1::R1::S1'], rpsItems: [] }
 */
export const associatedEntitiesToFormFields = (associatedEntities) => {
  const locations = [];
  const risks = [];
  const equipment = [];
  const sppItems = [];
  const rpsItems = [];

  for (const entity of associatedEntities || []) {
    switch (entity.entityType) {
      case 'LOCATION':
        locations.push(entity.locationId);
        break;
      case 'RISK':
        risks.push(`${entity.locationId}::${entity.riskId}`);
        break;
      case 'EQUIPMENT':
        equipment.push(`${entity.locationId}::${entity.riskId}::${entity.itemId}`);
        break;
      case 'SPP':
        sppItems.push(`${entity.locationId}::${entity.riskId}::${entity.itemId}`);
        break;
      case 'RELATED_PRIVATE_STRUCTURE':
        rpsItems.push(`${entity.locationId}::${entity.riskId}::${entity.itemId}`);
        break;
      default:
        break;
    }
  }

  return { locations, risks, equipment, sppItems, rpsItems };
};

/**
 * Converts flat form arrays → backend AssociatedEntity[].
 *
 * Parses composite IDs (delimited by '::') to extract locationId, riskId, itemId.
 */
export const formFieldsToAssociatedEntities = ({ locations, risks, equipment, sppItems, rpsItems }) => {
  const entities = [];

  for (const locationId of locations || []) {
    entities.push({ entityType: 'LOCATION', locationId });
  }

  for (const compositeId of risks || []) {
    const [locationId, riskId] = compositeId.split('::');
    entities.push({ entityType: 'RISK', locationId, riskId });
  }

  for (const compositeId of equipment || []) {
    const [locationId, riskId, itemId] = compositeId.split('::');
    entities.push({ entityType: 'EQUIPMENT', locationId, riskId, itemId });
  }

  for (const compositeId of sppItems || []) {
    const [locationId, riskId, itemId] = compositeId.split('::');
    entities.push({ entityType: 'SPP', locationId, riskId, itemId });
  }

  for (const compositeId of rpsItems || []) {
    const [locationId, riskId, itemId] = compositeId.split('::');
    entities.push({ entityType: 'RELATED_PRIVATE_STRUCTURE', locationId, riskId, itemId });
  }

  return entities;
};

/**
 * Hydrates a farm interested party from backend format (associatedEntities)
 * into form format (flat arrays) for use with chipArray fields.
 */
export const hydrateFarmPartyForForm = (party) => {
  if (!party) {
    return party;
  }
  const { fo41, ...rest } = party;
  const formFields = associatedEntitiesToFormFields(party.associatedEntities);
  return {
    ...rest,
    ...formFields,
    // #32582: mark a party that loaded from the backend with no associations (e.g. a UW removed the
    // structure it was attached to and scrubFarmPartyAssociatedEntities kept it per #30722). The
    // round-trip validation exempts these from the "at least one associated" gate so they don't block
    // previews, WITHOUT exempting a party the user clears in the editor (that one arrived with
    // associations, so it isn't marked). Stripped again by prepareFarmPartyForSave.
    arrivedWithoutAssociations: !(party.associatedEntities?.length > 0),
    // Mark a party that loaded from the backend with no description on a description-requiring
    // relationship — e.g. Forreston/IMT-migrated parties, whose importer never captured one. The
    // description gate (add-farm-interested-party.validation.js) exempts these so a later policy edit
    // isn't blocked by "Description is required" for a field that isn't on the Details tab, WITHOUT
    // exempting a newly added party (no marker) or one whose existing description the user clears
    // (arrived WITH one, so not marked). Mirrors arrivedWithoutAssociations (#32582). Stripped again
    // by prepareFarmPartyForSave.
    arrivedWithoutDescription: !party.description,
    // Same for the entity type: CFM-imported GL-108 parties GO counted but did not name carry none
    // (#36943). Records the relationship it arrived with — only one that requires an entity type — so the
    // gate exempts it only while staff keep that relationship; a lossPayee switched to GL-108 is not
    // exempt. Stripped again by prepareFarmPartyForSave.
    arrivedWithoutEntityTypeAs:
      RELATIONSHIPS_REQUIRING_ENTITY_TYPE.includes(party.relationship) && !party.entityType ? party.relationship : null,
    includeFO41: !!fo41,
    fo41Locations: fo41?.locations || [],
    fo41Risks: fo41?.risks || [],
    fo41Equipment: fo41?.equipment || [],
    fo41SppItems: fo41?.sppItems || [],
    fo41RpsItems: fo41?.rpsItems || [],
    fo41Description: fo41?.description || undefined
  };
};

/**
 * Prepares a farm interested party from form format (flat arrays)
 * into backend format (associatedEntities) for save/mutation.
 */
export const prepareFarmPartyForSave = (party) => {
  if (!party) {
    return party;
  }
  const {
    locations,
    risks,
    equipment,
    sppItems,
    rpsItems,
    includeFO41,
    fo41Locations,
    fo41Risks,
    fo41Equipment,
    fo41SppItems,
    fo41RpsItems,
    fo41Description,
    // #32582: form-only marker (see hydrateFarmPartyForForm) — never persisted to the backend.
    arrivedWithoutAssociations,
    // form-only marker (see hydrateFarmPartyForForm) — never persisted to the backend.
    arrivedWithoutDescription,
    // form-only marker (see hydrateFarmPartyForForm) — never persisted to the backend. #36943
    arrivedWithoutEntityTypeAs,
    ...rest
  } = party;
  const filtered = filterFieldsByRelationship({ locations, risks, equipment, sppItems, rpsItems }, party.relationship);
  const associatedEntities = formFieldsToAssociatedEntities(filtered);
  const fo41 = includeFO41
    ? {
        locations: fo41Locations || [],
        risks: fo41Risks || [],
        equipment: fo41Equipment || [],
        sppItems: fo41SppItems || [],
        rpsItems: fo41RpsItems || [],
        description: fo41Description
      }
    : null;
  return {
    ...rest,
    // GL-72 "occupant" = Resident of household; the rater (num_addtl_insured_resident)
    // requires isResident=true and there is no UI control for it, so derive it from the
    // same relationship list that shows the resident-of-household banner. Issue #31916.
    // A GL-108 keeps the flag it arrived with: CFM's GO migration carries the Commercial additional
    // insureds GO charges as "residing on premises" (AIRP) as resident GL-108s, and with no control
    // for it, clearing it on save would silently re-rate them as not residing (AINR). #36932
    isResident:
      RELATIONSHIPS_WITH_RESIDENT_INFO.includes(party.relationship) ||
      (party.relationship === 'mortgageeCoOwner' && party.isResident === true),
    associatedEntities,
    fo41
  };
};
